import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit } from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { SelectFieldSuffixComponent } from '../select-field-suffix.component';
import { ClearableFieldType } from './clearable-field.type';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { merge, takeUntil } from 'rxjs';
import { FieldLabelRowComponent } from './field-label-row.component';

const MONTH_LABELS = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December',
] as const;

@Component({
  selector: 'formly-field-month',
  template: `
    <mat-form-field
      class="w-full"
      [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      @if (props.label) {
        <mat-label>
          <app-field-label-row [label]="props.label" [required]="props.required"></app-field-label-row>
        </mat-label>
      }
      <mat-select
        [required]="props.required"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [formlyDataCy]="field">
        <mat-select-trigger>{{ labelFor(formControl.value) }}</mat-select-trigger>
        @for (month of months(); track month.value) {
          <mat-option [value]="month.value">
            <div class="flex items-center gap-2">
              <span class="min-w-0 flex-1">{{ month.label }}</span>
              @if (month.value === currentMonth) {
                <span
                  class="ml-auto shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-none bg-primary bg-opacity-12 text-primary-800">
                  Huidig
                </span>
              }
            </div>
          </mat-option>
        }
      </mat-select>
      <app-select-field-suffix
        matSuffix
        [field]="field"
        [hasValue]="hasClearableValue()"
        [disabled]="isDisabled()"
        [readOnly]="isReadOnly()"
        [ariaLabel]="clearAriaLabel()"
        (clearSelection)="clear()" />
      @if (field.props.description) {
        <mat-hint>{{ field.props.description }}</mat-hint>
      }
    </mat-form-field>
    @if (field.formControl.errors) {
      <app-formly-error [field]="field"></app-formly-error>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    SelectFieldSuffixComponent,
    FieldLabelRowComponent,
  ],
  styles: `
    ::ng-deep .mat-mdc-select-arrow {
      opacity: 0 !important;
    }
  `,
})
export class FormlyFieldMonthComponent extends ClearableFieldType implements OnInit {
  readonly currentMonth = new Date().getMonth() + 1;
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroy$ = injectDestroy();

  months = computed(() =>
    MONTH_LABELS.map((label, index) => ({
      value: index + 1,
      label,
    }))
  );

  ngOnInit() {
    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => this.#changeDetectorRef.markForCheck());

    if (this.field.defaultValue === 'currentMonth' && this.formControl.value === 'currentMonth') {
      this.formControl.setValue(this.currentMonth);
    }
  }

  labelFor(value: number | null | undefined): string {
    if (value == null) return '';
    return MONTH_LABELS[value - 1] ?? '';
  }
}
